import React, { useEffect, FC, useState } from 'react';
import Lottie from 'react-lottie';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PHSPSTModal from '@components/phs/test/pstGate/modals';
import { getAccessToken } from '@repository/AccessToken';
import LoginModal from '@components/loginModal';
import {
  useApplyPCPTermsCheckNew,
  useApplyPPPTermsCheckNew,
  useMutationTutorialTermsCheckNew,
} from '@repository/userTermsAgrees/useUserTermsAgrees';
import codeBarWhite from '@graphic/codeBarWhite.json';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useDchampsInfo, useDhampsFinalsCheck } from '@repository/dchamps/useDchamps';
import { JOBDA_SUPPORT_EMAIL } from '@domain/constant/contact';
import { PhsModalType } from '@domain/constant/phsModalType';
import { DchampsEventType } from '@domain/constant/dchamps/eventType';
import { RouteUrl } from '@utils/routeUtil';
import { queryClient } from '@utils/queryClient';
import useKcbIdentity from '@utils/hooks/useKcbIdentity';
import classnames from 'classnames/bind';
import styles from './phsPstGate.module.scss';
import DateFormat from '@utils/date/format';
import DateUtil from '@utils/date/util';
import NumberFormat from '@utils/number/format';
import { PSTApplyStatusKeys, usePSTApplyStatus, usePSTMaxApplyCount } from '@repository/phs/pst/apply/usePhsPstApply';
const cx = classnames.bind(styles);

interface IContinueBtnProps {
  chanceCount: number;
  estimatedEndDateTime?: string | null;
  onClick: () => void;
}

let _timer: ReturnType<typeof setInterval>;
const ContinueBtn: FC<IContinueBtnProps> = ({ chanceCount, estimatedEndDateTime, onClick }) => {
  const [second, setSecond] = useState(0);
  const timeText = NumberFormat.formatSecondToHHMMSS(second);

  useEffect(() => {
    //시간초 타이머
    if (estimatedEndDateTime) {
      if (_timer) clearInterval(_timer);

      _timer = setInterval(() => {
        const remainingSec = DateUtil.getLeftSeconds(estimatedEndDateTime);

        setSecond(remainingSec);
        if (remainingSec < 0) {
          //타이머 완료시 상태 재조회
          queryClient.refetchQueries(PSTApplyStatusKeys, { active: true, exact: true });
          clearInterval(_timer);
        }
      }, 1000);

      return () => clearInterval(_timer);
    }
  }, [estimatedEndDateTime]);

  return (
    <a className={cx('btn', 'phs_pstGate_btn_pooltrg')} role={'button'} onClick={onClick}>
      <div className={cx('textWrap')}>
        <span className={cx('badge')}>{chanceCount}회 남음</span>이어하기
        {estimatedEndDateTime && <span className={cx('time')}>남은시간 {timeText}</span>}
      </div>
    </a>
  );
};

const options = {
  loop: true,
  autoplay: true,
  animationData: codeBarWhite,
};

const PstTest = () => {
  const [modalState, setModalState] = useState<PhsModalType>(null);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const { isReady, replace, query } = useRouter();
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;

  const { data: dchampsPeriodData } = useDchampsInfo();
  const eventType = dchampsPeriodData?.progressFinal?.eventType;
  const { data: isFinalist } = useDhampsFinalsCheck(isLogin && !!eventType, eventType as DchampsEventType);
  const onlyFinalistJoin = dchampsPeriodData?.progressFinal && isFinalist;
  //디챔스 예선이 진행중이거나, 본선 진출자이면 이벤트 페이지 보여줌.
  const isDevChampsPeriod = dchampsPeriodData?.progressQualify || onlyFinalistJoin;
  const { data: pstMaxApplyCount } = usePSTMaxApplyCount();
  const { data: applyStatusData } = usePSTApplyStatus(isLogin);
  const { mutateAsync: checkTutorialTermsMutateAsync } = useMutationTutorialTermsCheckNew();
  const { mutateAsync: checkApplyPCPTermsMutateAsync } = useApplyPCPTermsCheckNew();
  const { mutateAsync: checkApplyPPPTermsMutateAsync } = useApplyPPPTermsCheckNew();
  const { isVerification } = useKcbIdentity({
    onError: () => setModalState(null),
    onSuccess: () => setModalState('TEST_TERMS'),
  });

  const openLoginModal = () => setLoginModalVisible(true);
  const closeLoginModal = () => setLoginModalVisible(false);
  const lastApplyStatus = applyStatusData?.lastApplyStatus || null;
  const chanceCount = applyStatusData?.chanceCount || 0;
  const isProgress = applyStatusData?.isProgress || false;

  useEffect(() => {
    if (isReady) {
      if (getAccessToken() && query.open === 'tutorial') {
        onClickTutorialApplyBtn();
        replace(RouteUrl.PHS_PST_TEST_GATE, RouteUrl.PHS_PST_TEST_GATE, { shallow: true });
      }
    }
  }, [isReady]);

  // 튜토리얼로 연습하기
  const onClickTutorialApplyBtn = async () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }

    //튜토리얼 약관 동의 했는지 체크
    const rs = await checkTutorialTermsMutateAsync();
    if (rs.existsNewTermsAgree) {
      setModalState('TUTORIAL_TERMS');
      return;
    }

    setModalState('TUTORIAL_GUIDE');
  };

  // 이어하기
  const onClickContinueBtn = () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }

    if (!lastApplyStatus) return;

    //응시 종료 시간이 지났으면 상태 재조회해서 응시 상태 변경해줌
    if (!DateUtil.isPastDate(new Date(lastApplyStatus.estimatedEndDateTime))) {
      queryClient.refetchQueries(PSTApplyStatusKeys, { active: true, exact: true });
    }

    setModalState('APPLY');
  };

  // 응시하기
  const onClickApplyBtn = async () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }

    //본인 인증 체크
    if (!isVerification) {
      setModalState('VERIFICATION');
      return;
    }

    if (chanceCount < 1) {
      setModalState('NO_MORE_CHANCE');
      return;
    }

    //약관 동의 여부 체크
    const applyPCPRs = await checkApplyPCPTermsMutateAsync();
    const applyPPPRs = await checkApplyPPPTermsMutateAsync();
    if (applyPCPRs.existsNewTermsAgree || applyPPPRs.existsNewTermsAgree) {
      setModalState('TEST_TERMS');
      return;
    }

    setModalState('TEST_GUIDE');
  };

  //phs_paccaGate_btn_pooltrg : GA 분석용  클래스
  const applyButtonEl = isProgress ? (
    <ContinueBtn
      chanceCount={chanceCount}
      estimatedEndDateTime={applyStatusData?.lastApplyStatus?.estimatedEndDateTime}
      onClick={onClickContinueBtn}
    />
  ) : (
    <a className={cx('btn', 'phs_pstGate_btn_pooltrg')} role={'button'} onClick={onClickApplyBtn}>
      <div className={cx('textWrap')}>
        {isLogin && <span className={cx('badge')}>{chanceCount}회 남음</span>}
        실전 응시하기
      </div>
    </a>
  );

  return (
    <div className={cx('abilityGateArea')}>
      <div className={cx('visualArea')}>
        <div className={cx('visualWrap')}>
          <Lottie options={options} />
        </div>
      </div>

      <div className={cx('infoArea')}>
        <div className={cx('infoInner')}>
          <strong className={cx('title')}>
            개발 구현 능력 검사는
            <br />
            개발을 통해 문제를 해결하는 능력을
            <br />
            진단하는 도구입니다.
          </strong>
          <em className={cx('subTitle')}>
            검사는 총 5단계까지 있으며,
            <br />
            단계가 진행될 때 마다 문제에 대한 조건이 추가됩니다.
          </em>
          <p className={cx('desc')}>
            검사는 약 100분 정도 소요되며, 매월 {pstMaxApplyCount}회의 응시기회가 제공됩니다.
          </p>
          {isDevChampsPeriod && (
            <p className={cx('desc', 'dchamps')}>
              디챔스 기간에는 상위 TOP30 대상으로 등수와 아이디가 랭킹관에 노출됩니다.
            </p>
          )}

          <div className={cx('btnArea')}>
            <a className={cx('btn')} role={'button'} onClick={onClickTutorialApplyBtn}>
              <div className={cx('textWrap')}>튜토리얼로 연습하기</div>
            </a>
            {applyButtonEl}
            <Link href={RouteUrl.PROFILE_PHS_PST} legacyBehavior>
              <a className={cx('btn')} target={'_blank'} rel="noreferrer" role="button">
                <div className={cx('textWrap')}>결과표 보러가기</div>
              </a>
            </Link>
          </div>
          <div className={cx('notice')}>
            응시에 문제가 생길 경우 <a href={`mailto:${JOBDA_SUPPORT_EMAIL}`}>{JOBDA_SUPPORT_EMAIL}</a>로 문의해주세요.
          </div>
        </div>
      </div>
      <PHSPSTModal modalState={modalState} setModalState={setModalState} />
      <LoginModal
        subTitle="채용정보 · 역량검사 · 매칭까지"
        title="취업기회 지금 바로 잡다!"
        visible={loginModalVisible}
        onClose={closeLoginModal}
      />
    </div>
  );
};

export default PstTest;
